import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import SamplePickerGate from './SamplePickerGate';
import FormControl from '../../common/FormControl';
import { useAutosave } from './useAutosave';
import { computeResponseReadOnly } from './responseShellTypes';
import type { AutosaveStatus, ProcedureResponseProps, ResponseRole } from './responseShellTypes';
import {
  responseService,
  transitionService,
  findingService,
  procedureEvidenceService,
  type SampleSet,
  type SampleItem,
  type Finding,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, {
  ITEM_TYPE_PHYSICAL_CHECK_RESPONSE,
  ITEM_TYPE_SAMPLE_ITEM,
} from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import NoDataFound from '../../common/NoDataFound';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';

/**
 * PhysicalCheckResponse — body for procedure_type='PhysicalCheck'.
 *
 * Per CONTEXT D-01 + D-03 (revised 2026-05-17):
 *   - Sample lifecycle owned by SamplePickerGate (Plan 05b-04). The body
 *     only sees the resolved (sampleSet, sampleItems) tuple via the gate's
 *     render prop.
 *   - One row per active sample_item.
 *   - Per row: book_qty / physical_qty / auto-computed difference /
 *     incharge_name / location.
 *   - Autosave via useAutosave(1000ms); saves through responseService
 *     ('PhysicalCheck', ...) keyed on existing response_id per sample_item_id.
 */

interface PhysicalCheckRow {
  sample_item_id: number;
  item_label: string;
  response_id?: number;
  book_qty: string;
  physical_qty: string;
  incharge_name: string;
  location: string;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

interface BodyHandleRef {
  status: AutosaveStatus;
  flush: () => Promise<void>;
}

interface BodyProps {
  instance: ProcedureResponseProps['instance'];
  sampleSet: SampleSet;
  sampleItems: SampleItem[];
  handleRef: React.MutableRefObject<BodyHandleRef>;
  onStatusChange: (s: AutosaveStatus) => void;
  /** G3 — true when the instance is Approved; disables all editing affordances. */
  readOnly?: boolean;
  /** Caller's role — checker ('approver') gets the editable reject control. */
  role: ResponseRole;
  /** G2 — report the number of lines carrying a reject_note up to the shell. */
  onRejectedCount: (n: number) => void;
  /** Submit gate — report the unanswered-lines reason (null = all answered). */
  onIncompleteReason: (reason: string | null) => void;
}

function difference(book: string, physical: string): string {
  const bn = parseFloat(book);
  const pn = parseFloat(physical);
  if (Number.isNaN(bn) || Number.isNaN(pn)) return '';
  return (pn - bn).toFixed(2);
}

function toNum(v: string): number | null {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

const PhysicalCheckBody: React.FC<BodyProps> = ({
  instance,
  sampleSet,
  sampleItems,
  handleRef,
  onStatusChange,
  readOnly = false,
  role,
  onRejectedCount,
  onIncompleteReason,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;

  const [rows, setRows] = useState<PhysicalCheckRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Plan 05b-05 / STAFF-06 — inline finding raise state.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemTypeId: number;
    itemRecordId: number | null;
  } | null>(null);
  // Phase 113 REQ-8 — per-row evidence, fetched once and grouped.
  const [evidenceByRow, setEvidenceByRow] = useState<Map<string, ProcedureEvidence[]>>(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<string, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id != null && ev.item_record_id != null) {
          const key = `${ev.item_type_id}-${ev.item_record_id}`;
          const bucket = map.get(key) ?? [];
          bucket.push(ev);
          map.set(key, bucket);
        }
      }
      setEvidenceByRow(map);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForRow = useCallback(
    (responseId: number | undefined, sampleItemId: number): Finding[] => {
      return findings.filter((f) => {
        if (responseId && f.item_type_id === ITEM_TYPE_PHYSICAL_CHECK_RESPONSE && f.item_record_id === responseId) {
          return true;
        }
        if (f.item_type_id === ITEM_TYPE_SAMPLE_ITEM && f.item_record_id === sampleItemId) {
          return true;
        }
        return false;
      });
    },
    [findings],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const responses = await responseService.list('PhysicalCheck', instance.id);
        const byItem = new Map<number, any>(
          responses
            .filter((r: any) => r.sample_item_id != null)
            .map((r: any) => [r.sample_item_id, r]),
        );
        const merged: PhysicalCheckRow[] = sampleItems.map(it => {
          const existing = byItem.get(it.id);
          return {
            sample_item_id: it.id,
            item_label: it.item_label ?? `Sample ${it.id}`,
            response_id: existing?.id,
            book_qty:
              existing?.book_quantity != null
                ? String(existing.book_quantity)
                : existing?.book_qty != null
                  ? String(existing.book_qty)
                  : '',
            physical_qty:
              existing?.physical_quantity != null
                ? String(existing.physical_quantity)
                : existing?.physical_qty != null
                  ? String(existing.physical_qty)
                  : '',
            incharge_name: existing?.incharge_name ?? '',
            location: existing?.location ?? '',
            reject_note: existing?.reject_note ?? '',
            dirty: false,
          };
        });
        if (!cancelled) setRows(merged);
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(
            e?.response?.data?.detail ?? e?.message ?? 'Failed to load physical-check responses.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id, sampleItems]);

  const save = useCallback(async () => {
    const dirty = rows.filter(
      r =>
        r.dirty &&
        !obsErrors[`${r.sample_item_id}::book_qty`] &&
        !obsErrors[`${r.sample_item_id}::physical_qty`] &&
        !obsErrors[`${r.sample_item_id}::incharge`] &&
        !obsErrors[`${r.sample_item_id}::location`],
    );
    const updates: Array<{ sample_item_id: number; response_id?: number }> = [];
    for (const r of dirty) {
      const book = toNum(r.book_qty);
      const physical = toNum(r.physical_qty);
      const diff = book != null && physical != null ? physical - book : null;
      const payload = {
        entity_id: entityId,
        procedure_instance_id: instance.id,
        population_id: sampleSet.population_id,
        sample_item_id: r.sample_item_id,
        book_quantity: book,
        physical_quantity: physical,
        difference_quantity: diff,
        incharge_name: r.incharge_name,
        location: r.location,
        created_by: currentUserId,
      };
      if (r.response_id) {
        await responseService.update('PhysicalCheck', r.response_id, payload);
        updates.push({ sample_item_id: r.sample_item_id, response_id: r.response_id });
      } else {
        const created = await responseService.create('PhysicalCheck', payload);
        updates.push({ sample_item_id: r.sample_item_id, response_id: created?.id });
      }
    }
    setRows(prev =>
      prev.map(r => {
        const u = updates.find(x => x.sample_item_id === r.sample_item_id);
        if (!u) return r;
        return { ...r, response_id: u.response_id ?? r.response_id, dirty: false };
      }),
    );
  }, [rows, obsErrors, entityId, currentUserId, instance.id, sampleSet.population_id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  // Keep the parent-readable handle current via a render-time ref write (no
  // state, so no extra render).
  handleRef.current = { status, flush };

  // UAT FIX H (flicker) — notify the parent ONLY when the status value changes.
  // `flush` changes identity per keystroke, so listing it in the deps re-ran this
  // effect and pushed an unchanged status to the parent → setState → re-render →
  // autosave-indicator flicker. Gate on the value to drop the redundant updates.
  const lastStatusRef = useRef(status);
  useEffect(() => {
    if (lastStatusRef.current !== status) {
      lastStatusRef.current = status;
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  // G2 — report rejected-line count up to the shell whenever it changes.
  useEffect(() => {
    onRejectedCount(rows.filter(r => r.reject_note && r.reject_note.trim()).length);
  }, [rows, onRejectedCount]);

  // Submit gate (2026-06-29) — every line must record an observed physical
  // quantity before submit. Empty physical_qty => not yet checked.
  useEffect(() => {
    const unanswered = rows.filter(r => !(r.physical_qty ?? '').trim()).length;
    onIncompleteReason(
      rows.length > 0 && unanswered > 0
        ? `Record a physical quantity for all ${rows.length} items before submitting — ${unanswered} still blank.`
        : null,
    );
  }, [rows, onIncompleteReason]);

  const updateRow = (idx: number, patch: Partial<PhysicalCheckRow>) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, dirty: true };
      return next;
    });
    markDirty();
  };

  // Per-field validation on blur (book/physical qty → 206, incharge & location
  // → 109). Mirrors the ChecklistResponse obsErrors idiom: clear on empty,
  // set/clear by validate() result, and the autosave filter skips rows with any
  // error.
  const handleFieldBlur = (rowKey: string, code: number, value: string, fallback: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const r = validate(code, value);
    if (!r.valid) setObsErrors(prev => ({ ...prev, [rowKey]: r.message ?? fallback }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  const COL_COUNT = 7;

  return (
    <>
      {loadError && (
        <div className="response-load-error">{loadError}</div>
      )}
      <div className="table-list">
        <div className="table-list-scroll">
          <table className="table-list-table">
            <colgroup>
              <col className="response-col-sno" />
              <col className="response-col-sample" />
              <col className="response-col-amount" />
              <col className="response-col-amount" />
              <col className="response-col-diff" />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className="table-list-th response-th-sno">S.No</th>
                <th className="table-list-th">Sample</th>
                <th className="table-list-th table-list-th--right">Book qty</th>
                <th className="table-list-th table-list-th--right">Physical qty</th>
                <th className="table-list-th table-list-th--right">Difference</th>
                <th className="table-list-th">Incharge</th>
                <th className="table-list-th">Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const rowFindings = findingsForRow(r.response_id, r.sample_item_id);
                const itemTypeId = r.response_id ? ITEM_TYPE_PHYSICAL_CHECK_RESPONSE : ITEM_TYPE_SAMPLE_ITEM;
                const itemRecordId = r.response_id ?? r.sample_item_id;
                const rowEvidence = evidenceByRow.get(`${itemTypeId}-${itemRecordId}`) ?? [];
                const diff = difference(r.book_qty, r.physical_qty);
                const responseValueSummary = `Book ${r.book_qty || '—'} · Physical ${
                  r.physical_qty || '—'
                } · Diff ${diff || '—'}${r.location ? ' · ' + r.location : ''}`;
                const openRemarks = () =>
                  setModalRow({
                    context: {
                      procedureName: instance.audit_procedure_name,
                      sampleLabel: r.item_label,
                      responseValue: responseValueSummary,
                    },
                    itemTypeId,
                    itemRecordId,
                  });
                return (
                  <React.Fragment key={r.sample_item_id}>
                    <tr className="table-list-row">
                      <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                      <td className="table-list-cell response-cell-question">
                        {/* REQ-7: question text LEFT, action cluster RIGHT, same flex row. */}
                        <div className="response-question-row">
                          <span className="response-question-text table-list-cell-title">{r.item_label}</span>
                          <div className="response-action-cluster response-action-cluster--inline">
                            <button
                              type="button"
                              className="response-row-action"
                              aria-label="Add remark"
                              title="Add remark"
                              disabled={readOnly}
                              onClick={openRemarks}
                            >
                              <MessageSquare size={14} />
                            </button>
                            <EvidenceCell
                              scope="row"
                              evidenceType={undefined}
                              entityId={entityId}
                              createdBy={currentUserId}
                              procedureInstanceId={instance.id}
                              itemTypeId={itemTypeId}
                              itemRecordId={itemRecordId}
                              questionLabel={r.item_label}
                              disabled={readOnly}
                              showCount={false}
                              onChanged={reloadEvidence}
                            />
                            <RejectNoteCell
                              responseId={r.response_id}
                              rejectNote={r.reject_note}
                              procedureType="PhysicalCheck"
                              role={role}
                              readOnly={readOnly}
                              onSaved={(note) =>
                                setRows(prev => prev.map((x, i) => (i === idx ? { ...x, reject_note: note } : x)))
                              }
                            />
                            <RaiseFindingButton
                              count={rowFindings.length}
                              showCount={false}
                              disabled={readOnly}
                              aria-label="Raise finding"
                              title="Raise a finding from this row"
                              onClick={() => { openRemarks(); }}
                            />
                          </div>
                        </div>
                        {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                        <InlineQuestionMeta
                          evidence={rowEvidence}
                          findings={rowFindings}
                          readOnly={readOnly}
                          onEvidenceRemoved={reloadEvidence}
                          onRemarkClick={readOnly ? undefined : openRemarks}
                        />
                      </td>
                      <td className="table-list-cell">
                        <FormControl.Input
                          type="number"
                          step="0.01"
                          data-testid={`INP_physical_check_response_${r.sample_item_id}_book_qty`}
                          value={r.book_qty}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const rowKey = `${r.sample_item_id}::book_qty`;
                            setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
                            updateRow(idx, { book_qty: e.target.value });
                          }}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleFieldBlur(`${r.sample_item_id}::book_qty`, 206, e.target.value, 'Must be a number of 0 or more.')
                          }
                          disabled={readOnly}
                          noMargin
                        />
                        {obsErrors[`${r.sample_item_id}::book_qty`] && (
                          <div className="response-inline-warning">
                            {obsErrors[`${r.sample_item_id}::book_qty`]}
                          </div>
                        )}
                      </td>
                      <td className="table-list-cell">
                        <FormControl.Input
                          type="number"
                          step="0.01"
                          data-testid={`INP_physical_check_response_${r.sample_item_id}_physical_qty`}
                          value={r.physical_qty}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const rowKey = `${r.sample_item_id}::physical_qty`;
                            setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
                            updateRow(idx, { physical_qty: e.target.value });
                          }}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleFieldBlur(`${r.sample_item_id}::physical_qty`, 206, e.target.value, 'Must be a number of 0 or more.')
                          }
                          disabled={readOnly}
                          noMargin
                        />
                        {obsErrors[`${r.sample_item_id}::physical_qty`] && (
                          <div className="response-inline-warning">
                            {obsErrors[`${r.sample_item_id}::physical_qty`]}
                          </div>
                        )}
                      </td>
                      <td className="table-list-cell table-list-cell--right">
                        <span className="response-cell-amount">{diff || '—'}</span>
                      </td>
                      <td className="table-list-cell">
                        <FormControl.Input
                          type="text"
                          data-testid={`INP_physical_check_response_${r.sample_item_id}_incharge`}
                          value={r.incharge_name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const rowKey = `${r.sample_item_id}::incharge`;
                            setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
                            updateRow(idx, { incharge_name: e.target.value });
                          }}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleFieldBlur(`${r.sample_item_id}::incharge`, 109, e.target.value, 'Invalid name.')
                          }
                          disabled={readOnly}
                          noMargin
                        />
                        {obsErrors[`${r.sample_item_id}::incharge`] && (
                          <div className="response-inline-warning">
                            {obsErrors[`${r.sample_item_id}::incharge`]}
                          </div>
                        )}
                      </td>
                      <td className="table-list-cell">
                        <FormControl.Input
                          type="text"
                          data-testid={`INP_physical_check_response_${r.sample_item_id}_location`}
                          value={r.location}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const rowKey = `${r.sample_item_id}::location`;
                            setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
                            updateRow(idx, { location: e.target.value });
                          }}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                            handleFieldBlur(`${r.sample_item_id}::location`, 109, e.target.value, 'Invalid location.')
                          }
                          disabled={readOnly}
                          noMargin
                        />
                        {obsErrors[`${r.sample_item_id}::location`] && (
                          <div className="response-inline-warning">
                            {obsErrors[`${r.sample_item_id}::location`]}
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <NoDataFound
            title="No samples to check"
            message="This procedure has no active sample items yet."
          />
        )}
      </div>

      {/* Plan 05b-05 / STAFF-06 — single ObservationModal + FindingDetailModal */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={modalRow?.itemTypeId ?? ITEM_TYPE_PHYSICAL_CHECK_RESPONSE}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
    </>
  );
};

const PhysicalCheckResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const handleRef = useRef<BodyHandleRef>({
    status: 'idle',
    flush: async () => {},
  });
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [rejectedCount, setRejectedCount] = useState(0);
  // Submit gate — reason bubbled up from the body (null = all answered).
  const [incompleteReason, setIncompleteReason] = useState<string | null>(null);
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  return (
    <ResponseShell
      instance={instance}
      procedureType="PhysicalCheck"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={autosaveStatus}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await handleRef.current.flush();
      }}
      onSubmit={async () => {
        await handleRef.current.flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async comment => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      <SamplePickerGate instance={instance} procedureType="PhysicalCheck">
        {(sampleSet, sampleItems) => (
          <PhysicalCheckBody
            key={instance.id}
            instance={instance}
            sampleSet={sampleSet}
            sampleItems={sampleItems}
            handleRef={handleRef}
            onStatusChange={setAutosaveStatus}
            readOnly={readOnly}
            role={role}
            onRejectedCount={setRejectedCount}
            onIncompleteReason={setIncompleteReason}
          />
        )}
      </SamplePickerGate>
    </ResponseShell>
  );
};

export default PhysicalCheckResponse;
